// Update Team: the manager keeps their team current. For each person: still in
// the same role, changed role, or left. Then anyone new is added, either an
// existing member of the organization or someone invited, with their team,
// role and access. (The intention of the prototype's Team Check, in this
// release's design.) Confirm Team and Add/Invite save to the backend
// (POST /v3/admin/capability/teams/confirm and …/teams/{id}/members).

import React, { useMemo, useState } from 'react';
import { Box, InputBase, Typography } from '@mui/material';
import type { Person } from '../../Program/components/program tabs/insights/insightsData';
import {
  BODY,
  BRANDY,
  DONE,
  FONT,
  INPUT_SX,
  LABEL,
  LINE,
  SCROLL,
  SECTION_TITLE,
  Segmented,
  SelectField,
  TEXT,
  TEXT_2,
  TEXT_3
} from '../designSystem';
import DosenTooltip from '../../../../../components/ui/DosenTooltip';
import { BrandCta } from '../learner/ProgramPanel';
import type { AddMemberInput, OperationsRoleView, TeamChangeInput } from '../../../../../api/capability';
import { formatDate } from '../../../../../utils/formatDate';
import { MOBILE_SX, useIsMobileApp } from '../../../../../mobile/mobileApp';

type Status = 'same' | 'changed' | 'left';
type Access = 'learner' | 'admin';

/** Someone in a team in view, with the role they hold there (or invited, not signed up yet). */
export interface OperationsPerson extends Person {
  roleId: string | null;
  invitePending: boolean;
}
/** A person in a team: the same person in two teams is two rows. */
const rowKey = (p: { id: string; teamId: string }) => `${p.id}|${p.teamId}`;
const STATUS: Array<{ value: Status; label: string }> = [
  { value: 'same', label: 'Same Role' },
  { value: 'changed', label: 'Changed Role' },
  { value: 'left', label: 'Left' }
];
const ACCESS: Array<{ value: Access; label: string }> = [
  { value: 'learner', label: 'Learner' },
  { value: 'admin', label: 'Admin' }
];
type AddKind = 'existing' | 'invite';
const ADD_KIND: Array<{ value: AddKind; label: string }> = [
  { value: 'existing', label: 'Select Existing Person' },
  { value: 'invite', label: 'Invite New' }
];

const GRID = 'minmax(180px, 1.2fr) minmax(140px, 1fr) minmax(160px, 1fr) minmax(420px, 2.4fr)';
const GRID_LINE = '1px solid #262626';
const cell = { display: 'flex', alignItems: 'center', minWidth: 0, p: '9px 12px' };
const headCell = { ...cell, bgcolor: '#232323', borderBottom: `1px solid ${LINE}` };
const cut = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const;
const validEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
// A field of the add form: the whole row on a phone.
const fieldBox = (width: number) => ({ width, [MOBILE_SX]: { width: '100%' } });

const UpdateTeam: React.FC<{
  /** The teams in view, and every team the manager can add someone to. */
  teams: Array<{ id: string; name: string; confirmedAt: string | null }>;
  /** Everyone in the teams in view (anyone invited included), with the role they hold now. */
  people: OperationsPerson[];
  /** Members of the organization who are not in the teams in view, to pick from. */
  others: Array<{ id: string; name: string; teamName: string }>;
  /** The teams' roles, from Capability Setup. */
  roles: OperationsRoleView[];
  onConfirm: (changes: TeamChangeInput[]) => Promise<unknown>;
  onAdd: (teamId: string, input: AddMemberInput) => Promise<unknown>;
}> = ({ teams, people, others, roles, onConfirm, onAdd }) => {
  // What the manager has marked and not yet confirmed.
  const [status, setStatus] = useState<Record<string, Status>>({});
  const [newRole, setNewRole] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const isMobileApp = useIsMobileApp();
  const statusOf = (p: OperationsPerson): Status => status[rowKey(p)] ?? 'same';
  const members = people.filter((p) => !p.invitePending);
  const ready = members.every((p) => statusOf(p) !== 'changed' || Boolean(newRole[rowKey(p)]));
  // Only roles whose capabilities and levels are confirmed are offered, as in Invite Team: the backend
  // refuses to add anyone to another one (docs/PRD-capabilities-roundup.md R15).
  const rolesOf = (teamId: string) =>
    roles.filter((r) => r.teamId === teamId && r.ready).map((r) => ({ id: r.id, name: r.name }));

  // Add New Team Member.
  const [kind, setKind] = useState<AddKind>('existing');
  const [existingId, setExistingId] = useState<string | null>(null);
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [email, setEmail] = useState('');
  const [teamId, setTeamId] = useState<string | null>(teams.length === 1 ? teams[0].id : null);
  const [roleId, setRoleId] = useState<string | null>(null);
  const [access, setAccess] = useState<Access>('learner');
  const addTeamId = teams.some((t) => t.id === teamId) ? teamId : null;
  const roleOptions = useMemo(
    () =>
      addTeamId ? roles.filter((r) => r.teamId === addTeamId && r.ready).map((r) => ({ id: r.id, name: r.name })) : [],
    [addTeamId, roles]
  );
  const who = kind === 'existing' ? Boolean(existingId) : Boolean(first.trim()) && validEmail(email);
  const canAdd = who && Boolean(addTeamId) && Boolean(roleId) && !saving;
  const addReason = !who
    ? kind === 'existing'
      ? 'Select a member first.'
      : 'Enter their first name and a valid email first.'
    : 'Choose their team and role first.';

  const add = async () => {
    if (!addTeamId || !roleId) return;
    const input: AddMemberInput =
      kind === 'existing'
        ? { userId: existingId as string, roleId, access }
        : { firstName: first.trim(), lastName: last.trim(), email: email.trim(), roleId, access };
    setSaving(true);
    try {
      await onAdd(addTeamId, input);
      setExistingId(null);
      setFirst('');
      setLast('');
      setEmail('');
      setRoleId(null);
    } catch {
      // The page shows why; what was typed stays.
    } finally {
      setSaving(false);
    }
  };

  const confirm = async () => {
    setSaving(true);
    try {
      await onConfirm(
        members.map((p) => ({ personId: p.id, teamId: p.teamId, status: statusOf(p), roleId: newRole[rowKey(p)] }))
      );
      setStatus({});
      setNewRole({});
    } catch {
      // The page shows why; the marks stay.
    } finally {
      setSaving(false);
    }
  };

  const lastConfirmed = teams
    .map((t) => t.confirmedAt)
    .filter((d): d is string => Boolean(d))
    .sort()
    .pop();

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* The team, person by person. */}
      {isMobileApp ? (
        // A phone: a card per person, the table's columns stacked in it.
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {people.length === 0 && (
            <Typography sx={{ ...BODY, p: '12px', color: TEXT_3 }}>No one in the selected teams yet.</Typography>
          )}
          {people.map((p) => {
            const s = statusOf(p);
            const key = rowKey(p);
            return (
              <Box
                key={key}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  p: '12px',
                  border: GRID_LINE,
                  borderRadius: '4px'
                }}
              >
                <Box sx={{ opacity: s === 'left' ? 0.5 : 1 }}>
                  <Typography sx={{ fontFamily: FONT, fontSize: 14, fontWeight: 600, color: TEXT }}>
                    {p.name}
                  </Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: TEXT_2, mt: '2px' }}>
                    {p.roleName} · {p.teamName}
                  </Typography>
                  {p.invitePending && (
                    <Typography sx={{ ...LABEL, color: BRANDY, mt: '6px' }}>Invite Pending</Typography>
                  )}
                </Box>
                {p.invitePending ? (
                  <Typography sx={{ fontFamily: FONT, fontSize: 12, color: TEXT_3 }}>
                    Joins this team in this role when they sign up.
                  </Typography>
                ) : (
                  <Box sx={{ alignSelf: 'flex-start' }}>
                    <Segmented
                      label={`${p.name} in ${p.teamName}`}
                      options={STATUS}
                      value={s}
                      onChange={(next) => setStatus((x) => ({ ...x, [key]: next }))}
                    />
                  </Box>
                )}
                {s === 'changed' && (
                  <SelectField
                    label={`New role for ${p.name}`}
                    placeholder="Choose the new role"
                    value={newRole[key] ?? null}
                    options={rolesOf(p.teamId).filter((role) => role.id !== p.roleId)}
                    onChange={(id) => setNewRole((x) => ({ ...x, [key]: id }))}
                  />
                )}
                {s === 'changed' && newRole[key] && (
                  <Typography sx={{ fontFamily: FONT, fontSize: 11, color: TEXT_3 }}>
                    Starts at Not assessed for this role.
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      ) : (
        <Box sx={{ ...SCROLL, flex: '0 1 auto', minHeight: 120, border: GRID_LINE, borderRadius: '4px' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: GRID, minWidth: 900 }}>
            {['Name', 'Team', 'Role', 'Status'].map((label, i) => (
              <Box
                key={label}
                sx={{ ...headCell, position: 'sticky', top: 0, zIndex: 1, borderLeft: i > 0 ? GRID_LINE : 'none' }}
              >
                <Typography sx={LABEL}>{label}</Typography>
              </Box>
            ))}
            {people.length === 0 && (
              <Typography sx={{ ...BODY, gridColumn: '1 / -1', p: '12px', color: TEXT_3 }}>
                No one in the selected teams yet.
              </Typography>
            )}
            {people.map((p, r) => {
              const s = statusOf(p);
              const key = rowKey(p);
              const rule = r < people.length - 1 ? GRID_LINE : 'none';
              const dim = s === 'left' ? 0.5 : 1;
              return (
                <React.Fragment key={key}>
                  <Box sx={{ ...cell, gap: '8px', borderBottom: rule, opacity: dim }}>
                    <Typography sx={{ ...cut, fontFamily: FONT, fontSize: 13, fontWeight: 600, color: TEXT }}>
                      {p.name}
                    </Typography>
                    {p.invitePending && (
                      <Typography sx={{ ...LABEL, flexShrink: 0, color: BRANDY }}>Invite Pending</Typography>
                    )}
                  </Box>
                  <Box sx={{ ...cell, borderBottom: rule, borderLeft: GRID_LINE, opacity: dim }}>
                    <Typography sx={{ ...cut, fontFamily: FONT, fontSize: 13, color: TEXT_2 }}>{p.teamName}</Typography>
                  </Box>
                  <Box sx={{ ...cell, borderBottom: rule, borderLeft: GRID_LINE, opacity: dim }}>
                    <Typography sx={{ ...cut, fontFamily: FONT, fontSize: 13, color: TEXT_2 }}>{p.roleName}</Typography>
                  </Box>
                  <Box sx={{ ...cell, gap: '10px', flexWrap: 'wrap', borderBottom: rule, borderLeft: GRID_LINE }}>
                    {p.invitePending ? (
                      <Typography sx={{ fontFamily: FONT, fontSize: 12, color: TEXT_3 }}>
                        Joins this team in this role when they sign up.
                      </Typography>
                    ) : (
                      <Segmented
                        small
                        label={`${p.name} in ${p.teamName}`}
                        options={STATUS}
                        value={s}
                        onChange={(next) => setStatus((x) => ({ ...x, [key]: next }))}
                      />
                    )}
                    {s === 'changed' && (
                      <Box sx={{ width: 200 }}>
                        <SelectField
                          label={`New role for ${p.name}`}
                          placeholder="Choose the new role"
                          value={newRole[key] ?? null}
                          options={rolesOf(p.teamId).filter((role) => role.id !== p.roleId)}
                          onChange={(id) => setNewRole((x) => ({ ...x, [key]: id }))}
                        />
                      </Box>
                    )}
                    {s === 'changed' && newRole[key] && (
                      <Typography sx={{ fontFamily: FONT, fontSize: 11, color: TEXT_3 }}>
                        Starts at Not assessed for this role.
                      </Typography>
                    )}
                  </Box>
                </React.Fragment>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Anyone new: an existing member of the organization, or someone invited. */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          pt: '14px',
          borderTop: '1px solid rgba(247, 148, 32, 0.45)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <Typography sx={SECTION_TITLE}>Add team member</Typography>
          <Segmented small options={ADD_KIND} value={kind} onChange={setKind} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '10px', flexWrap: 'wrap' }}>
          {kind === 'existing' ? (
            <Box sx={fieldBox(240)}>
              <Typography sx={{ ...LABEL, mb: '6px' }}>Person*</Typography>
              <SelectField
                label="Person"
                placeholder={others.length ? 'Select a person' : 'No one else'}
                value={existingId}
                options={others.map((p) => ({ id: p.id, name: `${p.name} (${p.teamName})` }))}
                onChange={setExistingId}
              />
            </Box>
          ) : (
            <>
              {[
                { label: 'First Name', value: first, set: setFirst, width: 150, required: true },
                { label: 'Last Name', value: last, set: setLast, width: 150, required: false },
                { label: 'Email', value: email, set: setEmail, width: 220, required: true }
              ].map((field) => (
                <Box key={field.label} sx={fieldBox(field.width)}>
                  <Typography sx={{ ...LABEL, mb: '6px' }}>
                    {field.label}
                    {field.required ? '*' : ''}
                  </Typography>
                  <InputBase
                    value={field.value}
                    onChange={(e) => field.set(e.target.value)}
                    inputProps={{ 'aria-label': field.label }}
                    sx={{ ...INPUT_SX, '&.Mui-focused': { borderColor: BRANDY } }}
                  />
                </Box>
              ))}
            </>
          )}
          <Box sx={fieldBox(180)}>
            <Typography sx={{ ...LABEL, mb: '6px' }}>Team*</Typography>
            <SelectField
              label="Team"
              placeholder="Select a team"
              value={addTeamId}
              options={teams}
              onChange={(id) => {
                setTeamId(id);
                setRoleId(null);
              }}
            />
          </Box>
          <Box sx={fieldBox(200)}>
            <Typography sx={{ ...LABEL, mb: '6px' }}>Role*</Typography>
            <SelectField
              label="Role"
              placeholder={
                addTeamId ? (roleOptions.length ? 'Select a role' : 'No confirmed roles yet') : 'Select a team first'
              }
              value={roleId}
              options={roleOptions}
              onChange={setRoleId}
            />
          </Box>
          <Box>
            <Typography sx={{ ...LABEL, mb: '6px' }}>Access</Typography>
            <Segmented options={ACCESS} value={access} onChange={setAccess} />
          </Box>
          <DosenTooltip title={canAdd ? '' : addReason} placement="bottom">
            <Box component="span" sx={{ display: 'inline-flex' }}>
              <BrandCta disabled={!canAdd} onClick={add}>
                {kind === 'existing' ? 'Add To Team' : 'Invite To Team'}
              </BrandCta>
            </Box>
          </DosenTooltip>
        </Box>
      </Box>

      {/* Confirming applies what is marked above. */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          pt: '12px',
          borderTop: `1px solid ${LINE}`
        }}
      >
        <Typography sx={{ flex: 1, fontFamily: FONT, fontSize: 12.5, color: lastConfirmed ? DONE : TEXT_3 }}>
          {lastConfirmed
            ? `Last confirmed on ${formatDate(lastConfirmed)}.`
            : ''}
        </Typography>
        <DosenTooltip
          title={ready ? '' : 'Choose the new role for everyone who changed role first.'}
          placement="bottom"
        >
          <Box component="span" sx={{ display: 'inline-flex' }}>
            <BrandCta disabled={!ready || members.length === 0 || saving} onClick={confirm}>
              {saving ? 'Saving...' : 'Confirm Team'}
            </BrandCta>
          </Box>
        </DosenTooltip>
      </Box>
    </Box>
  );
};

export default UpdateTeam;
